import { apiFetch, ApiClientError } from "@/lib/api-client";
import { supabase } from "@/lib/supabase";

// Phase 7 sub-bucket 15 — shared file-upload primitive.
//
// The codebase has two distinct upload shapes today:
//
//   1. `uploadFileViaSignedUrl` (3-step) — for direct-to-Supabase Storage
//      transfers. POST init → mints { storageKey, token } → SPA PUTs
//      bytes directly to Storage via the signed URL → POST finalize records
//      the row. Used by: portal forms / portal MDF / portal journeys / admin
//      content. The SPA never proxies bytes through the worker — keeps the
//      service-role key off the SPA, scales independently of worker CPU.
//
//   2. `uploadFileMultipart` (single POST) — for atomic small uploads
//      proxied through the worker. The worker validates size/MIME, transfers
//      to Storage, and patches the row in one handler. Used by: admin tier
//      badges (1 MB, image/* only). Splitting these into the 3-step shape
//      would create a new orphan-row failure mode legacy didn't have.
//
// Both helpers share `ApiClientError` for error semantics and reuse
// `apiFetch` / `supabase` for auth + envelope handling.
//
// The hook export `useFileUpload` is deliberately omitted — the three
// consumers each wrap their own `useMutation` with feature-specific
// invalidations + toasts. A generic hook would duplicate that contract
// without saving meaningful lines. If a fourth consumer ever lands with
// identical invalidation surface, hoist then.

const CONTENT_BUCKET = "content";

type UploadInitResponse = { storageKey: string; token: string };

export type UploadFileViaSignedUrlOptions = {
  /** POST endpoint that mints a signed upload URL. Worker returns { storageKey, token }. */
  initEndpoint: string;
  /** POST endpoint that records the attachment row after the direct PUT succeeds. */
  finalizeEndpoint: string;
  /**
   * Build the body for the finalize POST. Receives the storageKey from
   * step 1 and the original File. Implementations differ by feature
   * (forms passes `submission_id`; MDF passes `request_id`; journeys passes
   * `progress_id`). All include `storage_key + file_name + content_type +
   * size_bytes`.
   */
  buildFinalizeBody: (args: { storageKey: string; file: File }) => Record<string, unknown>;
  /**
   * Body for the upload-init POST. Defaults to `{ file_name, content_type,
   * size_bytes }` which matches the three existing route shapes; pass
   * explicitly when a feature needs an extra field (e.g. journeys' init
   * carries `progress_id` so the worker can check task ownership before
   * minting the URL).
   */
  buildInitBody?: (args: { file: File }) => Record<string, unknown>;
};

export async function uploadFileViaSignedUrl<TFinalizeResponse = { id: string }>(
  file: File,
  opts: UploadFileViaSignedUrlOptions,
): Promise<TFinalizeResponse> {
  const initBody = opts.buildInitBody?.({ file }) ?? {
    file_name: file.name,
    content_type: file.type || "application/octet-stream",
    size_bytes: file.size,
  };

  // Step 1 — mint signed upload URL.
  const init = await apiFetch<UploadInitResponse>(opts.initEndpoint, {
    method: "POST",
    body: JSON.stringify(initBody),
  });

  // Step 2 — direct PUT to Storage via the signed URL.
  const { error: uploadError } = await supabase.storage
    .from(CONTENT_BUCKET)
    .uploadToSignedUrl(init.storageKey, init.token, file, {
      contentType: file.type || "application/octet-stream",
      upsert: false,
    });
  if (uploadError) {
    throw new ApiClientError(0, "upload_failed", uploadError.message);
  }

  // Step 3 — record the row.
  const finalizeBody = opts.buildFinalizeBody({
    storageKey: init.storageKey,
    file,
  });
  return apiFetch<TFinalizeResponse>(opts.finalizeEndpoint, {
    method: "POST",
    body: JSON.stringify(finalizeBody),
  });
}

export type UploadFileMultipartOptions = {
  /** Worker endpoint that accepts the multipart form (relative to /api). */
  endpoint: string;
  /** Optional additional form fields written before the file part. */
  extraFields?: Record<string, string>;
};

/**
 * Single-POST multipart upload through the worker. The worker handles size +
 * MIME validation, writes to Storage with service-role, and patches the row
 * in the same handler. Use for small atomic uploads (≤1 MB images) where
 * the byte-proxy cost is acceptable and the orphan-row trade-off of the
 * 3-step pattern isn't worth it.
 */
export async function uploadFileMultipart<TResponse>(
  file: File,
  opts: UploadFileMultipartOptions,
): Promise<TResponse> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  const form = new FormData();
  for (const [key, value] of Object.entries(opts.extraFields ?? {})) {
    form.append(key, value);
  }
  form.append("file", file);

  const apiBase = import.meta.env.VITE_API_URL ?? "/api";
  const headers: HeadersInit = {};
  if (session?.access_token) {
    headers["authorization"] = `Bearer ${session.access_token}`;
  }

  const res = await fetch(`${apiBase}${opts.endpoint}`, {
    method: "POST",
    body: form,
    credentials: "include",
    headers,
  });
  if (!res.ok) {
    let payload: { error?: { code?: string; message?: string } } | null = null;
    try {
      payload = await res.json();
    } catch {
      // body was not JSON
    }
    throw new ApiClientError(
      res.status,
      payload?.error?.code ?? "upload_failed",
      payload?.error?.message ?? `Upload failed: ${res.status}`,
    );
  }
  return (await res.json()) as TResponse;
}
